/**
 * Browser client for the agent-chat sidecar's BUILDER contract (Agents v3 —
 * "Build your own agent"). POSTs the whole transcript to the sidecar's
 * `/builder/stream` (Authorization: Bearer <builder-token>) and pumps the
 * streamed frames:
 *
 *   delta      {text}                      live assistant text
 *   blueprint  {tool_use_id, blueprint}    a complete proposal (the review card)
 *   done       {stop_reason, text}         turn complete, with the full text
 *   error      {message}
 *
 * The sidecar is stateless: the browser owns the transcript and sends it
 * back every turn. An assistant turn that proposed a blueprint carries it
 * (+ its tool_use_id) so the sidecar can replay the tool call/result pair.
 */
import { parseFrame } from './agent-chat-stream';
import type { CustomAgentBlueprint } from '../api/client-reports/client-agents-api';

export interface BuilderTranscriptMessage {
  role: 'user' | 'assistant';
  text: string;
  blueprint?: CustomAgentBlueprint | null;
  tool_use_id?: string | null;
}

export type BuilderEvent =
  | { type: 'delta'; text: string }
  | { type: 'blueprint'; toolUseId: string; blueprint: CustomAgentBlueprint }
  | { type: 'done'; stopReason: string | null; text: string }
  | { type: 'error'; message: string };

export function builderFrameToEvent(event: string, data: string): BuilderEvent | null {
  let parsed: any;
  try {
    parsed = data ? JSON.parse(data) : {};
  } catch {
    return null;
  }
  switch (event) {
    case 'delta':
      return { type: 'delta', text: String(parsed.text ?? '') };
    case 'blueprint':
      return parsed && typeof parsed.blueprint === 'object' && parsed.blueprint
        ? {
            type: 'blueprint',
            toolUseId: String(parsed.tool_use_id ?? ''),
            blueprint: parsed.blueprint as CustomAgentBlueprint,
          }
        : null;
    case 'done':
      return {
        type: 'done',
        stopReason: parsed.stop_reason ?? null,
        text: String(parsed.text ?? ''),
      };
    case 'error':
      return { type: 'error', message: String(parsed.message ?? 'builder error') };
    default:
      return null;
  }
}

export interface StreamBuilderOptions {
  chatUrl: string;
  token: string;
  messages: BuilderTranscriptMessage[];
  onEvent: (event: BuilderEvent) => void;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}

/** Open one builder turn and pump its events until `done` / `error`. */
export async function streamBuilderTurn(opts: StreamBuilderOptions): Promise<void> {
  const doFetch = opts.fetchImpl ?? fetch;
  const res = await doFetch(opts.chatUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${opts.token}`,
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({ messages: opts.messages }),
    signal: opts.signal,
  });

  if (!res.ok || !res.body) {
    let detail = `Builder request failed (${res.status}).`;
    try {
      const body = await res.json();
      if (body?.error || body?.message) detail = String(body.message || body.error);
    } catch {
      /* non-JSON */
    }
    opts.onEvent({ type: 'error', message: detail });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let boundary: number;
      while ((boundary = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const parsed = parseFrame(frame);
        if (!parsed) continue;
        const ev = builderFrameToEvent(parsed.event, parsed.data);
        if (ev) {
          opts.onEvent(ev);
          if (ev.type === 'done' || ev.type === 'error') return;
        }
      }
    }
    const parsed = parseFrame(buffer);
    if (parsed) {
      const ev = builderFrameToEvent(parsed.event, parsed.data);
      if (ev) opts.onEvent(ev);
    }
  } finally {
    reader.releaseLock();
  }
}
